import { useRef } from "react";
import type { ToolAnimationState } from "../../hooks/useToolAnimation";
import moonIcon from "../../assets/moon.svg";
import antAnimation from "../../assets/animals/ant.svg";
import beeAnimation from "../../assets/animals/bee.svg";
import botAnimation from "../../assets/animals/bot.svg";
import eagleAnimation from "../../assets/animals/eagle.svg";
import foxAnimation from "../../assets/animals/fox.svg";
import leopardAnimation from "../../assets/animals/leopard.svg";
import monkeyAnimation from "../../assets/animals/monkey.svg";
import penguinAnimation from "../../assets/animals/penguin.svg";
import rabbitAnimation from "../../assets/animals/rabbit.svg";

const ANIMAL_ANIMATIONS = [
  antAnimation,
  beeAnimation,
  botAnimation,
  eagleAnimation,
  foxAnimation,
  leopardAnimation,
  monkeyAnimation,
  penguinAnimation,
  rabbitAnimation,
];

const pickAnimal = () =>
  ANIMAL_ANIMATIONS[Math.floor(Math.random() * ANIMAL_ANIMATIONS.length)];

interface ToolProgressProps {
  animation: ToolAnimationState;
}

export function ToolProgress({ animation }: ToolProgressProps) {
  // Pick a random animal each time the animation transitions inactive -> active,
  // so it stays stable across progress ticks but varies between thinking sessions.
  const wasActiveRef = useRef(false);
  const animalRef = useRef<string>(pickAnimal());
  if (animation.active && !wasActiveRef.current) {
    animalRef.current = pickAnimal();
  }
  wasActiveRef.current = animation.active;

  if (!animation.active) return null;

  const findingAnimation = animalRef.current;

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={animation.label}
      className="flex flex-col items-start gap-4"
    >
      {/* Animation visual */}
      <div className="relative flex items-center justify-center">
        <img
          src={findingAnimation}
          alt=""
          aria-hidden="true"
          className="w-40 lg:w-[240px] rounded-lg object-contain"
        />
      </div>

      {/* Label + percentage */}
      <div className="flex items-center gap-2">
        <img src={moonIcon} alt="" aria-hidden="true" className="size-4" />
        <span className="bg-gradient-to-r from-gradient-start to-gradient-end bg-clip-text text-body-sm lg:text-body-md font-medium text-transparent">
          {animation.label}
        </span>
        <span className="min-w-[4ch] tabular-nums text-body-sm text-grey">
          {animation.progress}%
        </span>
      </div>

      {/* Progress bar */}
      <div
        role="progressbar"
        aria-valuenow={animation.progress}
        aria-valuemin={0}
        aria-valuemax={100}
        className="h-1 w-40 lg:w-[240px] overflow-hidden rounded-full bg-black-50"
      >
        <div
          className="h-full rounded-full bg-gradient-to-r from-gradient-start to-gradient-end transition-[width] duration-100 ease-out"
          style={{ width: `${animation.progress}%` }}
        />
      </div>
    </div>
  );
}
